import {
  Button,
  Grid,
  Stack,
  Typography,
  Divider,
  Collapse,
  Box,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import HelpIcon from "@mui/icons-material/Help";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import ActivitiesField from "../../Reuseables/Fields/ActivitiesField";
import { preparationValidation } from "../../../helpers/validationSchemes";
import { editProject } from "../../../api/project";
import { RoleComponentGuard } from "../../Reuseables/Guards/RoleComponentGuard";

import { useMsal } from "@azure/msal-react";
import { DialogLeavingPage } from "../../Reuseables/Dialogs/LeavingPageDialog";
import AddActivitiesIntro from "../../Reuseables/Introductions/AddActivitiesIntro";
import { useTranslation } from "react-i18next";
import AreYouSureDialog from "../../Reuseables/Dialogs/AreYouSureDialog";
import InfoDialog from "../../Reuseables/Dialogs/InfoDialog";
import { useApplicationContext } from "../../ContextProviders/ApplicationContext";
import { CustomAccountInfo } from "../../../types/customAccountInfo";
import { Project } from "../../../types/project";
import { Role } from "../../../types/enums";
import { useOrganizationContext } from "../../ContextProviders/OrganizationContext";

type PreparationViewProps = {
  project: Project;
  setView: (view: number) => void;
};

export default function PreparationView({
  project,
  setView,
}: PreparationViewProps) {
  const [openIntroA, setOpenIntroA] = useState(true);
  const [openFinishDialog, setOpenFinishDialog] = useState(false);
  const [areYouSureDialog, setAreYouSureDialog] = useState(false);
  const [readyForSubmitDialog, setReadyForSubmitDialog] = useState(false);
  const { showSnackbar, showLoading, stopLoading } = useApplicationContext();
  const { updateProject } = useOrganizationContext();
  const { t } = useTranslation();
  const { instance } = useMsal();

  const account = instance?.getActiveAccount() as CustomAccountInfo;
  const role = account.idTokenClaims.role;

  const handleOpenAIntro = () => {
    setOpenIntroA(!openIntroA);
  };

  const handleAreYouSureClose = () => {
    setAreYouSureDialog(false);
  };

  const handleReadyForSubmitDialog = () => {
    setReadyForSubmitDialog(false);
  };

  const handleFinishDialogClose = () => {
    setOpenFinishDialog(false);
  };

  useEffect(() => {
    if (
      role !== Role.LocalCoordinator &&
      !project.skipLocalCoordinator &&
      project.readyForReview
    ) {
      setReadyForSubmitDialog(true);
    }

    // eslint-disable-next-line
  }, []);

  const _onSubmit = async (values: Project, actions: any) => {
    try {
      showLoading("loading.saving");
      let res = await editProject(values);
      actions.resetForm({ values });
      updateProject(res);
      if (role === Role.LocalCoordinator) {
        if (res.readyForReview) {
          setOpenFinishDialog(true);
          handleAreYouSureClose();
        }
      }
      showSnackbar(t("dialogs.successText1"), "success");
    } catch (error: unknown) {
      if (error instanceof Error) {
        showSnackbar(error.message, "error");
      } else {
        showSnackbar("Unknown error", "error");
      }
    } finally {
      stopLoading();
    }
  };

  return (
    <>
      <>
        <Formik
          key={project.projectCode}
          initialValues={project}
          onSubmit={_onSubmit}
          validationSchema={preparationValidation}
          validateOnChange={false}
        >
          {(props: any) => (
            <Form id={project.projectCode}>
              <DialogLeavingPage />
              <Grid
                container
                minHeight="60vh"
                mt={2}
                mb={2}
                sx={{
                  flexDirection: {
                    xs: "column",
                    sm: "column",
                    md: "column",
                    lg: "row",
                  },
                }}
              >
                <RoleComponentGuard
                  roles={[
                    Role.PrecureAdmin,
                    Role.SolutionOwner,
                    Role.LocalCoordinator,
                    Role.Partner
                    
                  ]}
                >
                  <Grid item xs>
                    <Grid container>
                      <Grid item flexGrow={1} />
                      <Grid item xs={12} sm={12} md={7} lg={6} xl={5}>
                        <Stack
                          mt={1}
                          direction={"row"}
                          onClick={handleOpenAIntro}
                          sx={{ ":hover": { cursor: "pointer" } }}
                        >
                          <Typography
                            color={"primary"}
                            fontSize={30}
                            variant="h5"
                          >
                            {t("labels.definingActivities")}
                          </Typography>
                          {openIntroA ? (
                            <ExpandLess sx={{ margin: 1 }} color={"primary"} />
                          ) : (
                            <HelpIcon
                              sx={{ margin: 1 }}
                              style={{ cursor: "pointer" }}
                              color={"primary"}
                            />
                          )}
                        </Stack>
                        <Collapse in={openIntroA} timeout="auto" unmountOnExit>
                          <AddActivitiesIntro hideForFM={true} />
                        </Collapse>
                        <Grid mt={2} item>
                          <ActivitiesField />
                        </Grid>
                      </Grid>
                      <Grid item flexGrow={1} />
                    </Grid>
                  </Grid>
                </RoleComponentGuard>
              </Grid>
              <RoleComponentGuard roles={[Role.LocalCoordinator]}>
                <Box mt={4} mb={4} display="flex" justifyContent={"center"}>
                  {t("preparationPage.text7")}
                </Box>
              </RoleComponentGuard>
              <Divider sx={{ marginTop: 2 }} />
              <Grid container mt={1} justifyContent="space-around">
                <RoleComponentGuard
                  roles={[
                    Role.PrecureAdmin,
                    Role.LocalCoordinator,
                    Role.SolutionOwner,
                    Role.Partner
                  ]}
                >
                  <Box>
                    <Button
                      id="saveBtn"
                      disabled={
                        !props.dirty ||
                        project.readyForReview ||
                        props.isSubmitting
                      }
                      variant="contained"
                      onClick={props.submitForm}
                    >
                      {t("buttons.saveChanges")}
                    </Button>
                  </Box>
                </RoleComponentGuard>

                <Box>
                  <>
                    <RoleComponentGuard roles={[Role.LocalCoordinator]}>
                      <Button
                        disabled={project.readyForReview || props.isSubmitting}
                        id="finishBtn"
                        variant="contained"
                        onClick={() => {
                          setAreYouSureDialog(true);
                        }}
                      >
                        {t("buttons.finish")}
                      </Button>
                    </RoleComponentGuard>
                    <RoleComponentGuard
                      roles={[Role.SolutionOwner, Role.PrecureAdmin, Role.Partner]}
                    >
                      <Button
                        variant="contained"
                        type="button"
                        disabled={props.isSubmitting}
                        onClick={async () => {
                          let possibleErrors = await props.validateForm();
                          if (Object.keys(possibleErrors).length === 0) {
                            await props.submitForm();
                            setView(2);
                          }
                          if (possibleErrors) {
                            props.setTouched({
                              ...props.touched,
                              ...possibleErrors,
                            });
                          }
                        }}
                      >
                        {t("buttons.reviewSubmit")}
                      </Button>
                    </RoleComponentGuard>
                  </>
                </Box>
              </Grid>
              <AreYouSureDialog
                title={t("dialogs.areYouSureHeader")}
                isDialogOpen={areYouSureDialog}
                handleClose={handleAreYouSureClose}
                isSubmitting={props.isSubmitting}
                onSubmit={() => {
                  props.setFieldValue("readyForReview", true, false);
                  props.submitForm();
                }}
                content={
                  <Typography>{t("dialogs.areYouSureText2")}</Typography>
                }
              />
            </Form>
          )}
        </Formik>

        <>
          <InfoDialog
            handleClose={handleReadyForSubmitDialog}
            isDialogOpen={readyForSubmitDialog}
            color="green"
            title={t("dialogs.readyForReview")}
            content={
              <>
                <Typography>{t("dialogs.readyForReviewText1")}</Typography>
                <Typography>{t("dialogs.areYouSureProceedText2")}</Typography>
              </>
            }
          />
          <InfoDialog
            handleClose={handleFinishDialogClose}
            isDialogOpen={openFinishDialog}
            color="green"
            title={t("dialogs.successHeader")}
            content={<Typography>{t("dialogs.successText2")}</Typography>}
          />
        </>
      </>
    </>
  );
}
